/**
 * RES-47 (épica D5) — Reportes de inventario: consumo, costo de venta (COGS), varianza y mermas.
 *
 * Helpers puros de la pantalla `/inventory/reports`: rango de fechas, parámetros de la consulta,
 * orden de tablas, filas para gráficos y export CSV. Los números vienen del backend (todo sale
 * del kardex); acá solo se ordenan, se recortan y se serializan.
 */

/** Vistas de la pantalla, en el orden de los tabs. */
export const REPORT_TABS = ['consumption', 'cogs', 'variance', 'waste'];

/** Agrupaciones del COGS. */
export const GROUP_BY_OPTIONS = ['day', 'week', 'month'];

/** Motivos de merma (mismo orden que el alta de movimientos). */
export const WASTE_REASON_CODES = [
  'EXPIRED',
  'BREAKAGE',
  'FAILED_PREP',
  'INTERNAL_USE',
  'COURTESY',
  'OTHER',
];

export const DEFAULT_RANGE_DAYS = 30;
export const MAX_RANGE_DAYS = 366;

/** `restaurantId` del consolidado de la cadena. */
export const ALL_RESTAURANTS = 'all';

/** Cantidad de barras en los gráficos de ranking. */
export const TOP_N = 10;

const DAY_MS = 24 * 60 * 60 * 1000;
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

const pad = (n) => String(n).padStart(2, '0');

/**
 * Fecha local `YYYY-MM-DD`.
 * @param {Date} date - Fecha
 * @returns {string} Fecha
 */
export const toDateInput = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/**
 * Rango por defecto: los últimos 30 días, hoy incluido.
 * @param {Date} [now] - Ahora
 * @returns {{from: string, to: string}} Rango
 */
export function defaultRange(now = new Date()) {
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const from = new Date(to);
  from.setDate(from.getDate() - (DEFAULT_RANGE_DAYS - 1));
  return { from: toDateInput(from), to: toDateInput(to) };
}

/**
 * Días entre dos fechas `YYYY-MM-DD` (mismo cálculo que el backend).
 * @param {string} from - Desde
 * @param {string} to - Hasta
 * @returns {number} Días
 */
export const rangeDays = (from, to) => (Date.parse(to) - Date.parse(from)) / DAY_MS;

/**
 * Valida el rango igual que el backend, para no pegarle con algo que va a rechazar.
 * @param {{from: string, to: string}} range - Rango
 * @returns {string|null} Código de error (`inventory.errors.*`) o `null`
 */
export function validateRange({ from, to } = {}) {
  if (!DATE_ONLY.test(from || '') || !DATE_ONLY.test(to || '')) return 'INVALID_DATE';
  if (Number.isNaN(Date.parse(from)) || Number.isNaN(Date.parse(to))) return 'INVALID_DATE';
  if (from > to) return 'INVALID_RANGE';
  if (rangeDays(from, to) > MAX_RANGE_DAYS) return 'RANGE_TOO_LONG';
  return null;
}

/**
 * Parámetros de `getInventoryReport` para una vista: `groupBy` solo en COGS e
 * `includeUncounted` solo en varianza.
 * @param {string} report - Vista
 * @param {{from: string, to: string, restaurantId?: string, groupBy?: string, includeUncounted?: boolean}} filters - Filtros
 * @returns {Object} Parámetros
 */
export function reportParams(report, { from, to, restaurantId, groupBy, includeUncounted } = {}) {
  const params = { from, to };
  if (restaurantId) params.restaurantId = restaurantId;
  if (report === 'cogs') params.groupBy = GROUP_BY_OPTIONS.includes(groupBy) ? groupBy : 'day';
  if (report === 'variance' && includeUncounted) params.includeUncounted = true;
  return params;
}

/**
 * "Todos los locales" solo si el usuario administra la organización, el plan tiene multi-local y
 * hay más de un local.
 * @param {{consolidatedRead?: boolean, multiRestaurant?: boolean, restaurantCount?: number}} ctx - Contexto
 * @returns {boolean} Si se ofrece el consolidado
 */
export const canViewAllRestaurants = ({ consolidatedRead, multiRestaurant, restaurantCount }) =>
  consolidatedRead === true && multiRestaurant === true && (restaurantCount || 0) > 1;

/**
 * Locales activos de la respuesta de `listRestaurants()` (array o `{ restaurants }`).
 * @param {*} data - Respuesta
 * @returns {Array<{restaurantId: string, name: string}>} Locales
 */
export function activeRestaurants(data) {
  const list = Array.isArray(data) ? data : data?.restaurants || [];
  return list.filter((r) => r && r.restaurantId && r.status !== 'deleted');
}

/**
 * Nombre de un local: el que manda el reporte, el de la lista de la org o el id.
 * @param {string} restaurantId - Local
 * @param {...Array<{restaurantId: string, name?: string}>} lists - Listas donde buscar
 * @returns {string} Nombre
 */
export function restaurantName(restaurantId, ...lists) {
  for (const list of lists) {
    const found = (list || []).find((r) => r.restaurantId === restaurantId);
    if (found?.name) return found.name;
  }
  return restaurantId;
}

/** Importe `{ value, estimatedValue, uncosted }` normalizado (nunca `undefined`). */
export const money = (m) => ({
  value: Number(m?.value) || 0,
  estimatedValue: Number(m?.estimatedValue) || 0,
  uncosted: Number(m?.uncosted) || 0,
});

/**
 * Si el reporte no tiene nada que mostrar en el rango.
 * @param {string} report - Vista
 * @param {Object|null} data - Respuesta
 * @returns {boolean} Vacío
 */
export function isEmptyReport(report, data) {
  if (!data) return true;
  if (report === 'consumption') return !(data.items || []).length;
  if (report === 'cogs') {
    const totals = data.totals || {};
    return !(data.periods || []).length && !totals.orders && !totals.grossCogs;
  }
  if (report === 'variance') return !(data.items || []).length;
  if (report === 'waste') return !(data.byItem || []).length && !(data.byReason || []).length;
  return true;
}

/**
 * Ordena filas por una clave (números antes que texto, `null` al final).
 * @param {Object[]} rows - Filas
 * @param {function(Object): *} accessor - Valor de orden
 * @param {'asc'|'desc'} [direction] - Dirección
 * @returns {Object[]} Copia ordenada
 */
export function sortRows(rows, accessor, direction = 'desc') {
  const sign = direction === 'asc' ? 1 : -1;
  return [...(rows || [])].sort((a, b) => {
    const va = accessor(a);
    const vb = accessor(b);
    const aNull = va === null || va === undefined || va === '';
    const bNull = vb === null || vb === undefined || vb === '';
    if (aNull || bNull) return aNull === bNull ? 0 : aNull ? 1 : -1;
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sign;
    return String(va).localeCompare(String(vb)) * sign;
  });
}

/**
 * Top N de insumos consumidos para el gráfico de barras.
 * @param {Object[]} items - Items del reporte de consumo
 * @param {'cost'|'quantity'} metric - Métrica
 * @param {number} [n] - Cantidad
 * @returns {Array<{itemId: string, name: string, unit: string, value: number}>} Filas
 */
export function consumptionTop(items, metric = 'cost', n = TOP_N) {
  const valueOf = (item) =>
    metric === 'quantity' ? Number(item.quantity) || 0 : money(item.cost).value;
  return sortRows(items, valueOf, 'desc')
    .map((item) => ({
      itemId: item.itemId,
      name: item.name,
      unit: item.unit,
      value: valueOf(item),
    }))
    .filter((row) => row.value > 0)
    .slice(0, n);
}

/**
 * Top N de varianzas en $ (por valor absoluto) para el gráfico divergente.
 * @param {Object[]} items - Items del reporte de varianza
 * @param {Function} nameOf - Etiqueta de la fila (p. ej. insumo + local)
 * @param {number} [n] - Cantidad
 * @returns {Array<{key: string, name: string, value: number}>} Filas
 */
export function varianceTop(items, nameOf, n = TOP_N) {
  return sortRows(items, (item) => Math.abs(money(item.varianceValue).value), 'desc')
    .filter((item) => money(item.varianceValue).value !== 0)
    .slice(0, n)
    .map((item) => ({
      key: `${item.restaurantId}|${item.itemId}`,
      name: nameOf(item),
      value: money(item.varianceValue).value,
    }));
}

/**
 * Filas del COGS por local (consolidado), ordenadas por costo de venta.
 * @param {Object} byRestaurant - restaurantId → buckets
 * @param {Function} nameOf - Nombre del local
 * @returns {Object[]} Filas `{ restaurantId, name, ...bucket }`
 */
export function cogsRestaurantRows(byRestaurant, nameOf) {
  return sortRows(
    Object.entries(byRestaurant || {}).map(([restaurantId, bucket]) => ({
      restaurantId,
      name: nameOf(restaurantId),
      ...bucket,
    })),
    (row) => Number(row.cogs) || 0,
    'desc'
  );
}

/**
 * Motivos presentes en el reporte de mermas, en el orden canónico (los desconocidos al final).
 * @param {Object} data - Reporte de mermas
 * @returns {string[]} Códigos
 */
export function wasteReasonsPresent(data) {
  const present = new Set();
  (data?.byReason || []).forEach((r) => present.add(r.reasonCode));
  (data?.byRestaurant || []).forEach((r) =>
    Object.keys(r.byReason || {}).forEach((c) => present.add(c))
  );
  const known = WASTE_REASON_CODES.filter((code) => present.has(code));
  const unknown = [...present].filter((code) => !WASTE_REASON_CODES.includes(code)).sort();
  return [...known, ...unknown];
}

/**
 * Etiqueta corta de un período del COGS (`YYYY-MM-DD` o `YYYY-MM`).
 * @param {string} period - Período
 * @param {string} groupBy - `day` | `week` | `month`
 * @param {string} [locale] - Locale de Intl
 * @returns {string} Etiqueta
 */
export function formatPeriod(period, groupBy, locale = 'es-AR') {
  const text = String(period || '');
  if (groupBy === 'month' && /^\d{4}-\d{2}$/.test(text)) {
    const [y, m] = text.split('-').map(Number);
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(y, m - 1, 1)));
  }
  if (DATE_ONLY.test(text)) {
    const [y, m, d] = text.split('-').map(Number);
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(y, m - 1, d)));
  }
  return text;
}

/**
 * Número con hasta 3 decimales (cantidades de insumo).
 * @param {number} value - Valor
 * @param {string} [locale] - Locale
 * @returns {string} Texto
 */
export function formatQty(value, locale = 'es-AR') {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(
    Object.is(n, -0) ? 0 : n
  );
}

/**
 * Porcentaje con un decimal (o `—` si no hay base).
 * @param {number|null} value - Porcentaje (0-100)
 * @param {string} [locale] - Locale
 * @returns {string} Texto
 */
export function formatPct(value, locale = 'es-AR') {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(Number(value))}%`;
}

/** Escapa una celda CSV (comillas, comas, punto y coma y saltos de línea). */
export const csvCell = (value) => {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return /[",\n;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * Serializa filas a CSV.
 * @param {Array<Array<*>>} rows - Filas (la primera es el encabezado)
 * @returns {string} CSV
 */
export const rowsToCsv = (rows) => rows.map((row) => row.map(csvCell).join(',')).join('\n');

/** Número crudo para el CSV (sin formato de moneda, para que la planilla sume). */
const num = (value) => (value === null || value === undefined ? '' : Number(value));

/**
 * Filas CSV de la vista actual. `label(key)` traduce los encabezados (`inventory.reports.csv.*`).
 * @param {string} report - Vista
 * @param {Object} data - Respuesta del reporte
 * @param {{label: function(string): string, restaurantName: function(string): string, reasonLabel: function(string): string}} ctx - Traducciones
 * @returns {Array<Array<*>>} Filas con encabezado
 */
export function reportCsvRows(report, data, { label, restaurantName: nameOf, reasonLabel }) {
  const consolidated = Boolean(data?.consolidated);
  if (report === 'consumption') {
    const header = [
      'item',
      'unit',
      'category',
      'sold',
      'reversed',
      'quantity',
      'cost',
      'estimated',
      'uncosted',
    ];
    const rows = [];
    (data?.items || []).forEach((item) => {
      const cost = money(item.cost);
      rows.push([
        item.name,
        item.unit,
        item.category || '',
        num(item.sold),
        num(item.reversed),
        num(item.quantity),
        cost.value,
        cost.estimatedValue,
        cost.uncosted,
        ...(consolidated ? [label('allRestaurants')] : []),
      ]);
      if (consolidated) {
        Object.entries(item.byRestaurant || {}).forEach(([restId, r]) => {
          const c = money(r.cost);
          rows.push([
            item.name,
            item.unit,
            item.category || '',
            '',
            '',
            num(r.quantity),
            c.value,
            c.estimatedValue,
            c.uncosted,
            nameOf(restId),
          ]);
        });
      }
    });
    return [[...header.map(label), ...(consolidated ? [label('restaurant')] : [])], ...rows];
  }
  if (report === 'cogs') {
    const fields = [
      'grossCogs',
      'reversals',
      'cogs',
      'revenue',
      'orders',
      'grossMargin',
      'grossMarginPct',
      'cogsPct',
      'uncosted',
    ];
    const line = (first, bucket) => [first, ...fields.map((f) => num(bucket?.[f]))];
    const rows = [[label('period'), ...fields.map(label)]];
    (data?.periods || []).forEach((p) => rows.push(line(p.period, p)));
    rows.push(line(label('total'), data?.totals));
    if (consolidated) {
      rows.push([]);
      rows.push([label('restaurant'), ...fields.map(label)]);
      Object.entries(data?.byRestaurant || {}).forEach(([restId, b]) =>
        rows.push(line(nameOf(restId), b))
      );
    }
    return rows;
  }
  if (report === 'variance') {
    const header = [
      'item',
      'unit',
      'restaurant',
      'opening',
      'receipts',
      'consumption',
      'waste',
      'otherOut',
      'adjustments',
      'theoretical',
      'actual',
      'variance',
      'variancePct',
      'varianceValue',
      'estimated',
      'counts',
      'lastCountAt',
    ];
    const rows = (data?.items || []).map((item) => {
      const v = money(item.varianceValue);
      return [
        item.name,
        item.unit,
        nameOf(item.restaurantId),
        num(item.opening),
        num(item.receipts),
        num(item.consumption),
        num(item.waste),
        num(item.otherOut),
        num(item.adjustments),
        num(item.theoretical),
        num(item.actual),
        num(item.variance),
        num(item.variancePct),
        v.value,
        v.estimatedValue,
        num(item.counts),
        item.lastCountAt || '',
      ];
    });
    return [header.map(label), ...rows];
  }
  if (report === 'waste') {
    const reasons = wasteReasonsPresent(data);
    const rows = [
      [
        label('item'),
        label('unit'),
        label('quantity'),
        label('movements'),
        label('cost'),
        label('estimated'),
        label('uncosted'),
        ...reasons.map(reasonLabel),
      ],
    ];
    (data?.byItem || []).forEach((item) => {
      const c = money(item.cost);
      rows.push([
        item.name,
        item.unit,
        num(item.quantity),
        num(item.movements),
        c.value,
        c.estimatedValue,
        c.uncosted,
        ...reasons.map((code) => num(item.reasons?.[code] ?? 0)),
      ]);
    });
    if ((data?.byRestaurant || []).length > 1 || consolidated) {
      rows.push([]);
      rows.push([
        label('restaurant'),
        label('movements'),
        label('cost'),
        ...reasons.map(reasonLabel),
      ]);
      (data?.byRestaurant || []).forEach((r) => {
        rows.push([
          nameOf(r.restaurantId),
          num(r.movements),
          money(r.cost).value,
          ...reasons.map((code) => money(r.byReason?.[code]).value),
        ]);
      });
    }
    return rows;
  }
  return [];
}

/**
 * Nombre del archivo del export.
 * @param {string} report - Vista
 * @param {{from: string, to: string}} range - Rango
 * @param {string} [scope] - `all` o id del local
 * @returns {string} Nombre
 */
export function reportCsvFileName(report, { from, to }, scope = '') {
  const suffix = scope === ALL_RESTAURANTS ? '_todos' : '';
  return `reporte_inventario_${report}_${from}_${to}${suffix}.csv`;
}

/** Locale de Intl según el idioma de la UI (mismo mapeo que `formatCurrency`). */
const LOCALES = { es: 'es-AR', en: 'en-US', pt: 'pt-BR' };

/**
 * Locale de Intl para un idioma de i18next.
 * @param {string} [language] - Idioma (`es`, `en-US`, …)
 * @returns {string} Locale
 */
export const intlLocale = (language) => LOCALES[String(language || 'es').slice(0, 2)] || 'es-AR';

/**
 * Moneda compacta para los ejes ("$ 1,2 M").
 * @param {number} value - Valor
 * @param {string} [locale] - Locale
 * @returns {string} Texto
 */
export function formatCompactCurrency(value, locale = 'es-AR') {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'ARS',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(value) || 0);
}
